// src/hooks/usePropertyVideoGeneration.ts
/**
 * Property Video Generation Hooks
 * Manages the video generation workflow with reactive status tracking
 */

import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { type Id } from '../../convex/_generated/dataModel';
import type { IPropertyScene, ISceneStatusSummary } from '@/@types/property';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get all videos for a property
 *
 * @param propertyId - The property listing ID, or undefined to skip
 * @returns Array of videos with loading state and latest video
 *
 * @example
 * ```tsx
 * const { videos, isLoading, latestVideo } = usePropertyVideos(propertyId);
 *
 * if (latestVideo && latestVideo.status === 'completed') {
 *   // Show the latest completed video
 * }
 * ```
 */
export function usePropertyVideos(propertyId: Id<'propertyListings'> | undefined) {
  const videos = useQuery(
    api.property.queries.getPropertyVideos,
    propertyId ? { propertyId } : 'skip'
  );

  // Videos are returned in descending order by creation time
  const videosArray = videos ?? [];
  const latestVideo = videosArray.length > 0 ? videosArray[0] : null;

  return {
    videos: videosArray,
    isLoading: videos === undefined,
    latestVideo,
  };
}

/**
 * Hook to get all scenes for a property video
 *
 * @param videoId - The property video ID, or undefined to skip
 * @returns Array of scenes with loading state and status summary
 *
 * @example
 * ```tsx
 * const { scenes, isLoading, statusSummary } = usePropertyScenes(videoId);
 *
 * const progress = statusSummary.completedCount / scenes.length;
 * ```
 */
export function usePropertyScenes(videoId: Id<'propertyVideos'> | undefined) {
  const scenes = useQuery(
    api.property.queries.getPropertyScenes,
    videoId ? { propertyVideoId: videoId } : 'skip'
  );

  const scenesArray = scenes ?? [];

  // Calculate status summary
  const statusSummary = calculateSceneStatusSummary(scenesArray);

  return {
    scenes: scenesArray,
    isLoading: scenes === undefined,
    statusSummary,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate status summary from an array of scenes
 *
 * @param scenes - Array of property scenes
 * @returns Status summary with counts for each status
 */
function calculateSceneStatusSummary(scenes: IPropertyScene[]): ISceneStatusSummary {
  const summary: ISceneStatusSummary = {
    pendingCount: 0,
    voiceoverGeneratingCount: 0,
    videoGeneratingCount: 0,
    completedCount: 0,
    failedCount: 0,
  };

  for (const scene of scenes) {
    switch (scene.status) {
      case 'pending':
        summary.pendingCount++;
        break;
      case 'voiceover_generating':
        summary.voiceoverGeneratingCount++;
        break;
      case 'video_generating':
        summary.videoGeneratingCount++;
        break;
      case 'completed':
        summary.completedCount++;
        break;
      case 'failed':
        summary.failedCount++;
        break;
    }
  }

  return summary;
}
